import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import ErrorMessage from '../../components/errormessage';
import CircularProgress from '@mui/material/CircularProgress';
import WebhookFormDef from '../../forms/webhook';
import {
    Block,
    FlexDiv,
    TextDefault,
    BigModal,
    Heading2,
    ThemedButton,
    DarkArrowTooltipIcon,
    TextTiny,
    Tag,
} from '@awarego-ui/components';
import TableClient from '../../components/table/TableClient';
import RequireRole from '../RequireRole';
import WebhookFormFields from '../../components/forms/WebhookFormFields';
import WebhookTestDialog from '../../components/forms/WebhookTestDialog';
import IntegrationDetailSidebar from '../../components/company/integration-detail-sidebar';
import Modal from 'react-modal';
import SendIcon from '@mui/icons-material/Send';
import services from '../../services';

function WebHooks() {
    const { commonStore, companyStore, store } = useStores();
    const { currentCompany } = store;
    const { webhooks, error, deletingWebhooks, loadingWebhooks } = companyStore;
    const rowsPerPage = 5;

    const [isModalOpen, setModalIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // The webhook whose test dialog is open, or null.
    const [testingWebhook, setTestingWebhook] = useState(null);

    useEffect(() => {
        Modal.setAppElement('body');
    }, []);

    useEffect(() => {
        if (currentCompany)
            companyStore.loadWebhooks(currentCompany.company_id);
    }, [currentCompany]);

    // useLocalObservable's factory runs only once (on mount), so the onSuccess
    // hook below captures whatever `doAddOrEditWebhook` reference existed then —
    // a stale closure over the first render's isSaving. Routing through a ref
    // (the "latest ref" pattern) makes onSuccess always call the current one.
    const doAddOrEditWebhookRef = useRef();

    const localStore = useLocalObservable(() => {
        let webhookForm = new WebhookFormDef(
            {},
            {
                onSuccess() {
                    doAddOrEditWebhookRef.current(this);
                },
                onError(form) {
                    console.error('error', form.errors());
                },
            }
        );

        return { webhookForm };
    });

    const doAddOrEditWebhook = async (form) => {
        // Same re-entrancy guard as the partner form: a double-click before
        // the first save resolves must not create/update the webhook twice.
        // (companyStore.createWebhook/updateWebhook already no-op on
        // concurrent calls via their own creatingWebhook/updatingWebhooks
        // flags, but the button itself wasn't reflecting that — this makes
        // the UI state match.)
        if (isSaving) return;
        setIsSaving(true);

        const activeCompanyId = store.currentCompany?.company_id;
        if (!activeCompanyId) {
            setIsSaving(false);
            return;
        }

        const values = form.values();
        const payload = {
            name: values.name,
            url: values.url,
            signatureSecret: values.signatureSecret,
            enabled: values.enabled,
            type: values.allTypes ? ['all'] : values.types,
        };

        try {
            if (values.id && values.id !== -1) {
                await companyStore.updateWebhook(
                    activeCompanyId,
                    values.id,
                    payload
                );
            } else {
                await companyStore.createWebhook(activeCompanyId, payload);
            }
            setModalIsOpen(false);
        } finally {
            setIsSaving(false);
        }
    };

    // Kept fresh every render so the mobx form's onSuccess hook (captured only
    // once at mount) always invokes the current handler, not a stale closure.
    doAddOrEditWebhookRef.current = doAddOrEditWebhook;

    const createNew = () => {
        openModal({
            id: -1,
            name: '',
            url: '',
            status: 1,
            definition: { eventName: [] },
        });
    };

    const openModal = (webhook) => {
        const eventNames = Array.isArray(webhook.definition?.eventName)
            ? webhook.definition.eventName
            : webhook.definition?.eventName
            ? [webhook.definition.eventName]
            : ['all'];
        const isAll = eventNames.includes('all');

        // Load per field via .set() (like the report) — update() breaks the
        // array field's binding.
        const f = localStore.webhookForm;
        f.$('id').set(webhook.id);
        f.$('name').set(webhook.name || '');
        f.$('url').set(webhook.url || '');
        f.$('signatureSecret').set(webhook.definition?.signatureSecret || '');
        f.$('enabled').set('value', webhook.status === 1);
        f.$('allTypes').set('value', isAll);
        f.$('types').set(isAll ? [] : eventNames);
        f.$('types').set('disabled', isAll);
        f.resetValidation();
        f.validate();
        setModalIsOpen(true);
    };

    const deleteKey = (id) => {
        commonStore.showConfirm(
            'Do you really want to delete this Webhook?',
            'Delete',
            'Delete Webhook?',
            () => {
                companyStore.deleteWebhook(id, {
                    companyId: currentCompany.company_id,
                });
            }
        );
    };

    const columns = useMemo(() => {
        return [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: observer((x) => {
                    return (
                        <>
                            {x.value && x.value.length > 0 ? (
                                x.value
                            ) : (
                                <Tag text="No Name" />
                            )}{' '}
                        </>
                    );
                }),
            },
            {
                Header: 'Url',
                accessor: 'url',
                Cell: observer((x) => {
                    return <TextTiny>{x.value}</TextTiny>;
                }),
                disableGlobalFilter: true,
            },
            {
                Header: '',
                accessor: 'id',
                // FlexDiv wraps by default, so a long name or url squeezes this
                // cell until the action icons stack onto several lines. Keeping
                // them on one line also makes the column's min-content width the
                // width of the icons, so the table stops shrinking it.
                Cell: observer((x) => (
                    <FlexDiv $noWrap style={{ whiteSpace: 'nowrap' }}>
                        {deletingWebhooks.includes(x.value) ? (
                            <CircularProgress />
                        ) : (
                            buildExtraActionsColumn(x.row.original)
                        )}
                    </FlexDiv>
                )),
                disableGlobalFilter: true,
            },
        ];
    }, [
        commonStore,
        companyStore,
        currentCompany.company_id,
        deletingWebhooks,
    ]);

    const buildExtraActionsColumn = (row) => {
        return (
            <>
                <RequireRole>
                    {/* Only an enabled webhook can be tested - a disabled one
                        is refused by the API, since delivery is off for it. */}
                    {row.status === 1 && (
                        <DarkArrowTooltipIcon
                            customFunction={() => {
                                setTestingWebhook(row);
                            }}
                            row={row}
                            title="Test Webhook"
                            icon={<SendIcon />}
                        ></DarkArrowTooltipIcon>
                    )}
                    <DarkArrowTooltipIcon
                        customFunction={() => {
                            openModal(row);
                        }}
                        row={row}
                        title="Edit"
                        icon={'edit'}
                    ></DarkArrowTooltipIcon>
                    <DarkArrowTooltipIcon
                        customFunction={() => deleteKey(row.id)}
                        row={row}
                        title="Delete Webhook"
                        icon={'delete'}
                    ></DarkArrowTooltipIcon>
                </RequireRole>
            </>
        );
    };

    return (
        <FlexDiv>
            <Block flexGrow="9" mr="20">
                <ErrorMessage error={error} />
                <FlexDiv column>
                    <Heading2 mb={8}>Webhooks</Heading2>
                    <TextDefault>
                        This Webhooks are designed to receive notifications on
                        various actions and modifications of trainings and
                        assessments.
                    </TextDefault>
                </FlexDiv>
                <FlexDiv pt="32" pb="32">
                    <ThemedButton
                        onClick={createNew}
                        mode="primary"
                        text="Create Webhook"
                    />
                </FlexDiv>
                {loadingWebhooks && <CircularProgress />}
                <TableClient
                    columns={columns}
                    initiallyLoaded={loadingWebhooks === false}
                    data={[...webhooks]}
                    noItemFoundText={'No existing keys'}
                    hidePagination={webhooks.length <= rowsPerPage}
                />
            </Block>

            <FlexDiv column flexGrow="3" style={{ alignSelf: 'flex-start' }}>
                <IntegrationDetailSidebar
                    href="https://help.awarego.com/webhooks"
                    description="Learn more on how to use webhooks."
                />
            </FlexDiv>

            <BigModal
                ariaHideApp={false}
                isOpen={isModalOpen}
                width={600}
                onRequestClose={() => setModalIsOpen(false)}
                heading={
                    localStore.webhookForm.$('id').value === -1
                        ? 'Create Webhook'
                        : 'Edit Webhook'
                }
                modalContent={
                    <WebhookFormFields form={localStore.webhookForm} />
                }
                modalFooterContent={
                    <Fragment>
                        <ThemedButton
                            mode="primary"
                            text={
                                isSaving
                                    ? 'Saving'
                                    : localStore.webhookForm.$('id').value ===
                                      -1
                                    ? 'Create'
                                    : 'Save'
                            }
                            $saving={isSaving}
                            $center={true}
                            disabled={
                                !localStore.webhookForm.isValid || isSaving
                            }
                            onClick={(e) => {
                                localStore.webhookForm.submit(e);
                            }}
                        />
                        <ThemedButton
                            mode="secondary"
                            onClick={() => {
                                setModalIsOpen(false);
                            }}
                            text="Close"
                        />
                    </Fragment>
                }
            />

            <WebhookTestDialog
                isOpen={!!testingWebhook}
                webhook={testingWebhook}
                onClose={() => setTestingWebhook(null)}
                onSend={(eventName) =>
                    services.Companies.WebhooksService(
                        currentCompany.company_id
                    ).test(testingWebhook.id, eventName)
                }
            />
        </FlexDiv>
    );
}

export default observer(WebHooks);
